import axios from 'axios'
import { ENV } from '../config'
import constants from '../global/constants'
import jwtDecoder from 'jwt-decode'
import storage from '../mixins/storageMixin'

// In the developer environment the login page shows a 'donut' icon that controls a dev widget.
// The widget allows a developer to update the api endpoint for testing purposes for their session.
const ENDPOINT_URL = storage.methods.getStorage('endpoint_url') || ENV.ENDPOINT_URL
const REFRESH_TOKEN_THRESHOLD = ENV.REFRESH_TOKEN_THRESHOLD || 60

export const api = axios.create({
  baseURL: ENDPOINT_URL,
})

let isRefreshing = false
let pendingRequests = []

api.interceptors.request.use(async (config) => {

  // Check if the request should skip bearer token authentication
  if (config.skipAuth) {
    return config
  }

  let accessToken = storage.methods.getStorage(constants.storageKeys.AccessToken)
  if (isTokenExpired(accessToken)) {
    storage.methods.delStorage(constants.storageKeys.AccessToken)
    if (!isRefreshing) {
      try {
        isRefreshing = true
        accessToken = await refreshToken(accessToken)
        resolvePendingRequests(accessToken)
      } catch (error) {
        // Reject all pending requests in case of an error
        pendingRequests.forEach(({ reject }) => reject(error))

        // Reject original request
        console.error('Failed to refresh token.', error)
        throw error
      } finally {
        // Clear the queue and reset refreshing state
        pendingRequests = []
        isRefreshing = false
      }
    } else {
      return addPendingRequest({ ...config, headers: { ...config.headers, 'Authorization': `Bearer ${accessToken}` } })
    }
  }

  // Set the access token for the current request
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`
  }

  return config
}, (error) => Promise.reject(error))

async function refreshToken(accessToken) {
  const refreshConfig = { withCredentials: true }
  const { data } = await axios.post(`${ENDPOINT_URL}/session/refresh-token/`, null, refreshConfig)

  if (data.errors) {
    throw new Error(data.errors[0].message)
  }

  await storage.methods.setStorage(constants.storageKeys.AccessToken, data.token)
  accessToken = storage.methods.getStorage(constants.storageKeys.AccessToken)
  return accessToken
}

function resolvePendingRequests(accessToken) {
  pendingRequests.forEach(({ request, resolve }) => {
    // Update the access token in the pending request header
    const updatedRequest = { ...request, headers: { ...request.headers, 'Authorization': `Bearer ${accessToken}` } }
    resolve(updatedRequest)
  })
}

function addPendingRequest(request) {
  return new Promise((resolve, reject) => {
    pendingRequests.push({ request, resolve, reject })
  })
}

function isTokenExpired(token) {
  if (!token) {
    return true
  }

  try {
    const decoded = jwtDecoder(token)
    const currentTime = Date.now() / 1000 // time in seconds

    if (!decoded.exp) {
      throw new Error('Invalid token. Expiration not found')
    }

    const remainingTime = decoded.exp - currentTime
    return remainingTime < REFRESH_TOKEN_THRESHOLD
  } catch (error) {
    console.error('Invalid token:', error)
    return true
  }
}

export default {
  methods: {
    async graphQuery(queryString, variables, skipAuth = false) {
      try {
        const response = await api.post('/graphql/',
          {
            query: queryString,
            variables: variables,
          },
          {
            skipAuth
          })

        const { data } = response
        if (data.errors) {
          throw new Error(data.errors[0].message)
        } else {
          return data.data
        }
      } catch (error) {
        // Our Link backend has middleware on some calls to map the JWT token from the request header
        // into an object called IContextData. If the token has expired it automatically returns 'Not Authorised!'
        // in it's error response whereas, those endpoints that don't have this middleware go through a helper method.
        // The helper method returns an error message that includes the words 'jwt expired'.
        // In either case, we capture both case here and log them out
        if (error.message.includes('jwt expired') || error.message.includes('Not Authorised!') || error.message.includes('401')) {
          this.displayAlert('fail', 'Your session has expired, please sign in again.')
          this.logout()
          return
        }

        throw error
      }
    },
    async login(credentials) {
      try {
        const result = await axios({
          url: `${ENDPOINT_URL}/session/login`,
          method: 'post',
          data: credentials,
          withCredentials: true,
        })

        const { token } = result.data
        await storage.methods.setStorage(constants.storageKeys.AccessToken, token)

        // On a successful session login, the refresh token cookie is updated
        // Any previous logout records are wiped to start anew.
        storage.methods.delStorage(constants.storageKeys.Logout)

        this.loadUserAuthorizations()
      } catch (error) {
        throw new Error(`Failed to login: ${error.message}`)
      }
    },
    async logout() {
      try {
        await axios.post(`${ENDPOINT_URL}/session/logout/`, null, { withCredentials: true })
      } catch (error) {
        console.error(error)
      }

      this.cleanUserAuthorization()
      storage.methods.delStorage(constants.storageKeys.AccessToken)
      storage.methods.delStorage(constants.storageKeys.LastSessionActive)

      // On a logout, localStorage is updated to alert all open tabs to log out of their sessions as well
      await storage.methods.setStorage(constants.storageKeys.Logout, Date.now())
      console.log('Logged out')

      // I noticed console errors when the login page is being set multiple times due to some failure
      // We do a check before proceeding to add to the router
      if (this.$route.name !== 'login') {
        this.$router.push({ name: 'login' })
      }
    },
  },
}
